import { useEffect, useMemo, useState } from "react";
import type { StatusReportTemplate } from "@forgepoint/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useStatusReportTemplatesStore } from "@/store/statusReportTemplatesStore";
import { StatusReportTemplateRow } from "./StatusReportTemplateRow";
import { TemplateSettingsSheet } from "./TemplateSettingsSheet";
import { TEMPLATE_ORDER } from "./templateDisplay";

/**
 * The Report Settings panel: the org's four status-report templates, each with
 * an enabled switch and a settings sheet. Visible only to holders of
 * settings.edit (the tab is permission-gated); users pick which report
 * they personally receive in Personal Settings.
 */
export function StatusReportSettingsPanel() {
  const templateMap = useStatusReportTemplatesStore(
    (state) => state.templateMap,
  );
  const isLoadingList = useStatusReportTemplatesStore(
    (state) => state.isLoadingList,
  );
  const error = useStatusReportTemplatesStore((state) => state.error);
  const fetchTemplates = useStatusReportTemplatesStore(
    (state) => state.fetchTemplates,
  );

  const [editing, setEditing] = useState<StatusReportTemplate | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const ordered = useMemo(() => {
    const templates = [...templateMap.values()];
    return TEMPLATE_ORDER.flatMap((key) =>
      templates.filter((template) => template.templateKey === key),
    );
  }, [templateMap]);

  if (isLoadingList && ordered.length === 0) {
    return (
      <div className="space-y-6 pt-4">
        <Skeleton className="h-5 w-32 ml-2" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="px-4 py-3.5">
                <Skeleton className="h-4 w-64 mb-2" />
                <Skeleton className="h-3 w-96" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (error && ordered.length === 0) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      <div>
        <h3 className="text-lg font-semibold tracking-tight pl-2">
          Daily Status Reports
        </h3>
        <p className="text-sm text-muted-foreground pl-2">
          Enable and tune each report template. Users choose which report they
          receive in their personal settings; content is always scoped to what
          the recipient can see.
        </p>
      </div>
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {ordered.map((template) => (
            <StatusReportTemplateRow
              key={template.templateKey}
              template={template}
              onEdit={(target) => {
                setEditing(target);
                setSheetOpen(true);
              }}
            />
          ))}
        </div>
      </Card>

      <TemplateSettingsSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        template={editing}
      />
    </div>
  );
}
